import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box"
import Autocomplete from '@mui/material/Autocomplete';
import TextField from "@mui/material/TextField";
import { grey } from "@mui/material/colors";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, isUndefined } from "lodash";

import cycleCalendarService from "@plannel/services/data/cycle-calendar-service";
import calendarGroupService from "@plannel/services/data/calendar-group-service";
import CalendarModal from "@plannel/components/modal/CalendarModal"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import DataState from "@plannel/components/aggrid/DataState"
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, EditButton, FilterButton } from "@plannel/components/ActionIconButton";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import reduxUtil from "@plannel/utils/redux-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "../utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { cycleUoms } = CodeUtil;
const CycleCalendarMaster = ({ t, title, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [sortParams, setSortParams] = useState({});
  const [calendarGrp, setCalendarGrp] = useState(reduxViewState?.calendarGrp ?? null);
  const [calendarGrps, setCalendarGrps] = useState([]);

  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [cycleCalendars, setCycleCalendars] = useState([]);
  const [cycleCalendar, setCycleCalendar] = useState({});

  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);


  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disabledColor = { background: grey[300], opacity: 0.5 };

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const setWomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "M") && !data.domObj?.onDates) {
      return {};
    }

    return disabledColor;
  }

  const setDomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "W") || (isEqual(data.cycleCd, "M") && !data.domObj?.onDates)) {
      return {};
    }

    return disabledColor;
  }

  const valueFormatter = (params) => {
    return params.value?.name ? params.value.name : "";
  }

  const columnDefs = [
    {
      headerName: "calendarName", field: "name", type: ["nonEditableColumn"], width: 200, filterType: "string"
    },
    {
      headerName: "repeatEvery", groupId: "repeatEvery",
      children: [
        {
          headerName: "intervalVal", field: "intervalVal", type: ["numberColumn", "nonEditableColumn"], width: 90, filterType: "number",
          valueFormatter: params => isEqual(params.data?.cycleCd, "S") ? "" : params.value,
          cellStyle: params => {
            const { data } = params;
            if (isEqual(data.cycleCd, "S")) {
              return disabledColor;
            }
            return {};
          }
        },
        {
          headerName: "cycleCd", field: "cycleCd", type: ["nonEditableColumn"], width: 100, cellClass: 'stringType', filterType: "string", filterOption: cycleUoms.map(uom => uom.text),
          valueFormatter: params => {
            const { value } = params;
            const uomIndex = cycleUoms.findIndex(uom => isEqual(uom.value, value));
            if (uomIndex > -1) {
              const cycleUom = cycleUoms[uomIndex];
              return t(cycleUom.text);
            }

            return value;
          }
        }
      ]
    },
    {
      headerName: "period", groupId: "period",
      children: [
        { headerName: "startDate", field: "startDate", colId: "startDate", type: ["nonEditableColumn"], width: 120, filterType: "date" },
        { headerName: "endDate", field: "endDate", colId: "endDate", type: ["nonEditableColumn"], width: 120, filterType: "date" }
      ]
    },
    { headerName: "calendarGrpName", field: "calendarGroup", type: ["nonEditableColumn"], width: 150, valueFormatter, comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending), filterType: "string"},
    { headerName: "descTxt", field: "descTxt", type: ["nonEditableColumn"], width: 200, filterType: "string" },
    // Month of Year
    {
      headerName: "monthOfYear", field: "domObj", type: ["nonEditableColumn"], width: 130, filterType: "number",
      sortable: false,
      valueFormatter: params => {
        const { value } = params;
        if (value?.onMonth || value?.onMonth === 0) {
          return value.onMonth + 1;
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y")) {
          return {};
        }
        return disabledColor;
      }
    },
    // Day of Month
    {
      headerName: "dateOfMonth", field: "domObj", type: ["nonEditableColumn"], width: 120, filterType: "number",
      sortable: false,
      valueFormatter: params => {
        const { value } = params;

        if (value?.onDate) {
          return value.onDate;
        } else if (value?.onDates) {
          const dates = value.onDates;
          return dates.join(", ");
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y") || (isEqual(data.cycleCd, "M") && data.domObj?.onDates)) {
          return {};
        }
        return disabledColor;
      }
    },
    // Week of Month
    {
      headerName: "weekOfMonth", groupId: "wom",
      children: [
        { headerName: "first", field: "wom1wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor, filterType: "boolean" },
        { headerName: "second", field: "wom2wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor, filterType: "boolean" },
        { headerName: "third", field: "wom3wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor, filterType: "boolean" },
        { headerName: "fourth", field: "wom4wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor, filterType: "boolean" },
        { headerName: "fifth", field: "wom5wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor, filterType: "boolean" },
      ]
    },
    // {
    //   headerName: "disaggRatio", groupId: "wom",  hide: true,
    //   children: [
    //     { headerName: "first", field: "wom1wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "second", field: "wom2wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "third", field: "wom3wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "fourth", field: "wom4wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
    //     { headerName: "fifth", field: "wom5wRatio", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor }
    //   ]
    // },
    // Day of Week
    {
      headerName: "dayOfWeek", groupId: "dow",
      children: [
        { headerName: "mon", field: "dowMonFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor, filterType: "boolean" },
        { headerName: "tue", field: "dowTueFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor, filterType: "boolean" },
        { headerName: "wed", field: "dowWedFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor, filterType: "boolean" },
        { headerName: "thu", field: "dowThuFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor, filterType: "boolean" },
        { headerName: "fri", field: "dowFriFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor, filterType: "boolean" },
        { headerName: "sat", field: "dowSatFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor, filterType: "boolean" },
        { headerName: "sun", field: "dowSunFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor, filterType: "boolean" },
      ]
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn", "nonEditableColumn"], width: 80, filterType: "boolean" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  const retrieveCalendarGroup = () => {
    calendarGroupService.getAll()
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          setCalendarGrps(results);

          if (reduxViewState?.calendarGrp) {
            const reduxCalendarGrp = results.find(result => isEqual(result.id, reduxViewState.calendarGrp.id));
            isUndefined(reduxCalendarGrp) ? setCalendarGrp(null) : setCalendarGrp(reduxCalendarGrp);
          }
        } else {
          setCalendarGrps([]);
          setCalendarGrp(null);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (calendarGrp?.name) {
      searchFilters.calendarGrpName = calendarGrp.name;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveCycleCalendar = () => {
    const params = getRequestParams();
    cycleCalendarService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setCycleCalendars(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { calendarGrp, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const addCycleCalendar = () => {
    setCycleCalendar({});
    setCalendarModalOpen(true);
  }

  const deleteCycleCalendar = () => {
    const cycleCalendarIds = [];
    gridRef.current.api.getSelectedRows().forEach(data => {
      cycleCalendarIds.push(data.id);
    });

    if (cycleCalendarIds.length > 0) {
      cycleCalendarService.remove(cycleCalendarIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveCycleCalendar();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
    }
  }

  const editCycleCalendar = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (isEmpty(selectedRows)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedRow") });
    } else {
      setCycleCalendar(selectedRows[0]);
      setCalendarModalOpen(true);
    }
  }

  const handleDialog = action => {
    if (action === "DELETE") {
      deleteCycleCalendar();
    }
  }

  const onGridReady = params => {
    retrieveCalendarGroup();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveCycleCalendar();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveCycleCalendar();
    }

    return () => {
      setCycleCalendars([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setCycleCalendars([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, calendarGrp]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <Autocomplete
          options={calendarGrps}
          getOptionLabel={option => option.name}
          value={calendarGrp}
          onChange={(_, newValue) => setCalendarGrp(newValue)}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          size="small"
          sx={{ minWidth: 230 }}
          renderInput={(params) => (
            <TextField {...params} label={t("calendarGrpName")} />
          )}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={cycleCalendarService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addCycleCalendar} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.cycleCalendar, 'delete')}` })} />
          <EditButton onClick={editCycleCalendar} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={cycleCalendars}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellDoubleClicked={editCycleCalendar}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <CalendarModal
        activeControl
        calendarGrps={calendarGrps}
        modalOpen={calendarModalOpen}
        setModalOpen={setCalendarModalOpen}
        selectedCycle={cycleCalendar}
        setSnackInfo={setSnackInfo}
        retrieve={retrieveCycleCalendar}
      />
    </>
  );
};

export default withTranslation()(CycleCalendarMaster);